import Tracker, { SanitizeLevel, type Options as OpenReplayOptions } from '@openreplay/tracker';
import type { RuntimeEnv } from '../config/runtimeEnv';

export type OpenReplayFeatureConfig = {
  enabled?: boolean;
  captureConsole?: boolean;
  captureErrors?: boolean;
  captureNetwork?: boolean;
};

export type OpenReplayMetadata = {
  appVersion?: string;
  brand?: string;
  environment: string;
  tenantId?: string;
  userId: string;
  username?: string;
};

type OpenReplayNetworkData =
  NonNullable<NonNullable<OpenReplayOptions['network']>['sanitizer']> extends (data: infer Data) => unknown
    ? Data
    : never;

type StartOpenReplayArgs = {
  featureConfig: OpenReplayFeatureConfig;
  runtimeEnv: RuntimeEnv;
  projectKey?: string | null;
};

type ResolveOpenReplayProjectKeyArgs = {
  runtimeEnv: Pick<RuntimeEnv, 'openReplayProjectKey'>;
  projectKey?: string | null;
};

const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token', 'x-csrf-token']);

const SENSITIVE_QUERY_PARAMS = new Set(['access_token', 'code', 'password', 'refresh_token', 'session', 'token']);

const SENSITIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[data-openreplay-hidden]',
  '[data-sensitive]',
  '[name*="password" i]',
  '[name*="token" i]',
  '[name*="card" i]',
  '[name*="cvv" i]',
  '[name*="iban" i]',
  '[name*="document" i]',
  '[autocomplete="cc-number"]',
  '[autocomplete="cc-csc"]',
].join(',');

let tracker: Tracker | null = null;
let isStarted = false;
let isStarting = false;
let identifiedUserId: string | null = null;
let pendingIdentityMetadata: OpenReplayMetadata | null = null;

export const resolveOpenReplayProjectKey = ({
  projectKey,
  runtimeEnv,
}: ResolveOpenReplayProjectKeyArgs): string | undefined => {
  const marketplaceProjectKey = projectKey?.trim();

  if (marketplaceProjectKey) {
    return marketplaceProjectKey;
  }

  return runtimeEnv.openReplayProjectKey?.trim() || undefined;
};

export const canStartOpenReplay = ({ featureConfig, projectKey, runtimeEnv }: StartOpenReplayArgs): boolean => {
  return Boolean(
    featureConfig.enabled && resolveOpenReplayProjectKey({ projectKey, runtimeEnv }) && runtimeEnv.openReplayIngestPoint
  );
};

export const sanitizeOpenReplayNetworkData = (data: OpenReplayNetworkData): OpenReplayNetworkData | null => {
  const sanitizedUrl = sanitizeUrl(data.url);

  if (!sanitizedUrl) {
    return null;
  }

  return {
    ...data,
    url: sanitizedUrl,
    request: {
      body: null,
      headers: sanitizeHeaders(data.request.headers),
    },
    response: {
      body: null,
      headers: sanitizeHeaders(data.response.headers),
    },
  };
};

export const startOpenReplay = ({ featureConfig, projectKey, runtimeEnv }: StartOpenReplayArgs): void => {
  const effectiveProjectKey = resolveOpenReplayProjectKey({ projectKey, runtimeEnv });
  const ingestPoint = runtimeEnv.openReplayIngestPoint;

  if (
    isStarted ||
    isStarting ||
    typeof window === 'undefined' ||
    !featureConfig.enabled ||
    !effectiveProjectKey ||
    !ingestPoint
  ) {
    return;
  }

  isStarting = true;

  const startingTracker = new Tracker({
    captureExceptions: featureConfig.captureErrors ?? true,
    consoleMethods: featureConfig.captureConsole === false ? [] : ['log', 'info', 'warn', 'error', 'debug'],
    defaultInputMode: 1,
    ingestPoint,
    network: {
      captureInIframes: false,
      capturePayload: false,
      disabled: !featureConfig.captureNetwork,
      failuresOnly: false,
      ignoreHeaders: false,
      sanitizer: sanitizeOpenReplayNetworkData,
      sessionTokenHeader: false,
    },
    obscureInputDates: true,
    obscureInputEmails: true,
    obscureInputNumbers: true,
    obscureTextEmails: true,
    obscureTextNumbers: true,
    projectKey: effectiveProjectKey,
    domSanitizer: (node) => (node.matches(SENSITIVE_SELECTOR) ? SanitizeLevel.Hidden : SanitizeLevel.Plain),
  });
  tracker = startingTracker;

  void startingTracker
    .start()
    .then((result) => {
      if (tracker !== startingTracker) {
        return;
      }

      if (result.success) {
        isStarted = true;
        isStarting = false;
        if (pendingIdentityMetadata) {
          const metadata = pendingIdentityMetadata;
          pendingIdentityMetadata = null;
          try {
            identifyTrackerUser(startingTracker, metadata);
          } catch {
            // Identification failure must not invalidate an otherwise active tracker.
          }
        }
        return;
      }

      isStarting = false;
      pendingIdentityMetadata = null;
      tracker = null;
    })
    .catch(() => {
      if (tracker === startingTracker) {
        isStarting = false;
        pendingIdentityMetadata = null;
        tracker = null;
      }
    });
};

export const identifyOpenReplayUser = (metadata: OpenReplayMetadata): void => {
  if (tracker && isStarting) {
    pendingIdentityMetadata = metadata;
    return;
  }

  if (!tracker || !isStarted || identifiedUserId === metadata.userId) {
    return;
  }

  identifyTrackerUser(tracker, metadata);
};

export const resetOpenReplayUser = (): void => {
  identifiedUserId = null;
  pendingIdentityMetadata = null;
};

export const stopOpenReplay = (): void => {
  tracker?.stop();
  tracker = null;
  isStarted = false;
  isStarting = false;
  identifiedUserId = null;
  pendingIdentityMetadata = null;
};

const identifyTrackerUser = (activeTracker: Tracker, metadata: OpenReplayMetadata): void => {
  activeTracker.setUserID(metadata.userId);
  setOpenReplayMetadata(activeTracker, metadata);
  identifiedUserId = metadata.userId;
};

const setOpenReplayMetadata = (activeTracker: Tracker, metadata: OpenReplayMetadata): void => {
  Object.entries(metadata).forEach(([key, value]) => {
    if (value) {
      activeTracker.setMetadata(key, value);
    }
  });
};

const sanitizeHeaders = (headers: Record<string, string>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADER_NAMES.has(key.toLowerCase()) ? '[redacted]' : value,
    ])
  );
};

const sanitizeUrl = (rawUrl: string): string | null => {
  try {
    const url = new URL(rawUrl, window.location.origin);

    SENSITIVE_QUERY_PARAMS.forEach((paramName) => {
      if (url.searchParams.has(paramName)) {
        url.searchParams.set(paramName, '[redacted]');
      }
    });

    return url.toString();
  } catch {
    return null;
  }
};
export const __resetOpenReplayForTests = (): void => {
  tracker = null;
  isStarted = false;
  isStarting = false;
  identifiedUserId = null;
  pendingIdentityMetadata = null;
};
